import { useEffect, useRef, useState } from 'react';
import { api } from '../api/client';
import { Stack, Row, Text, Button, Input, Panel, List, ListItem, Tag, Divider, EmptyState, Card } from '../ui';
import clsx from 'clsx';

type Kind = 'blacklist' | 'whitelist';

// ─── CSV Import Modal ────────────────────────────────────────────────────────
function CsvImportModal({ kind, onClose, onDone }: { kind: Kind; onClose: () => void; onDone: () => void }) {
  const [mode, setMode] = useState<'file' | 'paste'>('file');
  const [csvText, setCsvText] = useState('');
  const [parsed, setParsed] = useState<any[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ inserted: number; skipped_duplicates: number; errors: any[] } | null>(null);
  const dropRef = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  function parseCSVPreview(text: string) {
    const lines = text.split(/\r?\n/).filter(l => l.trim());
    if (lines.length < 2) return null;
    const headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/"/g, ''));
    const rows = lines.slice(1, 6).map(line => {
      const cols = line.split(',').map(c => c.trim().replace(/^"|"$/g, ''));
      const obj: any = {};
      headers.forEach((h, i) => { obj[h] = cols[i] ?? ''; });
      return obj;
    });
    return rows;
  }

  function handleText(text: string) {
    setCsvText(text);
    const preview = parseCSVPreview(text);
    setParsed(preview);
    setResult(null);
  }

  function handleFile(file: File) {
    const reader = new FileReader();
    reader.onload = e => handleText(e.target?.result as string ?? '');
    reader.readAsText(file);
  }

  function onDrop(e: React.DragEvent) {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  }

  async function submit() {
    if (!csvText.trim()) return;
    setBusy(true);
    try {
      const r = await api.importListCSV(kind, csvText);
      setResult(r);
      if (r.inserted > 0) onDone();
    } catch (e: any) {
      alert('Import failed: ' + e.message);
    } finally {
      setBusy(false);
    }
  }

  const API_URL = (import.meta.env.VITE_API_URL as string) || 'https://api.ardacious.app';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/20 backdrop-blur-[2px]">
      <div className="bg-bg hairline rounded-sm w-full max-w-xl mx-4 max-h-[90vh] overflow-auto">
        <div className="hairline-b px-4 h-12 flex items-center justify-between">
          <Text variant="small" className="font-medium">Import CSV &mdash; {kind}</Text>
          <button onClick={onClose} className="text-muted hover:text-ink text-sm">close</button>
        </div>

        <div className="p-4">
          <Stack gap={3}>
            <Row gap={1}>
              <a
                href={`${API_URL}/lists/${kind}/template.csv`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-accent hover:underline"
              >
                download template
              </a>
              <Text variant="label">&mdash; required columns: name, reason. optional: country, identifier_type, identifier_value, source.</Text>
            </Row>

            <Row gap={1}>
              <button onClick={() => setMode('file')} className={clsx('text-xs px-2 py-1 rounded-sm hairline transition-colors', mode === 'file' ? 'bg-ink text-bg' : 'text-muted hover:text-ink')}>upload file</button>
              <button onClick={() => setMode('paste')} className={clsx('text-xs px-2 py-1 rounded-sm hairline transition-colors', mode === 'paste' ? 'bg-ink text-bg' : 'text-muted hover:text-ink')}>paste csv</button>
            </Row>

            {mode === 'file' ? (
              <div
                ref={dropRef}
                onDrop={onDrop}
                onDragOver={e => e.preventDefault()}
                onClick={() => fileRef.current?.click()}
                className="border-2 border-dashed border-muted/30 rounded-sm p-8 text-center cursor-pointer hover:border-accent/50 transition-colors"
              >
                <Text variant="small" muted>drag & drop a .csv file here, or click to browse</Text>
                <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={e => e.target.files?.[0] && handleFile(e.target.files[0])} />
              </div>
            ) : (
              <textarea
                className="w-full h-32 hairline rounded-sm p-2 text-xs font-mono bg-bg resize-none"
                placeholder={"name,reason\nAcme Corp,Fraud history\n"}
                value={csvText}
                onChange={e => handleText(e.target.value)}
              />
            )}

            {parsed && parsed.length > 0 && !result && (
              <Stack gap={1}>
                <Text variant="micro">preview (first 5 rows)</Text>
                <div className="overflow-x-auto hairline rounded-sm">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="hairline-b bg-whisper">
                        {Object.keys(parsed[0]).map(h => (
                          <th key={h} className="px-2 py-1 text-left text-muted font-normal">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {parsed.map((row, i) => (
                        <tr key={i} className="hairline-b last:border-b-0">
                          {Object.values(row).map((v: any, j) => (
                            <td key={j} className="px-2 py-1 text-ink max-w-[140px] truncate">{v || <span className="text-muted">—</span>}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Stack>
            )}

            {result && (
              <Card className="bg-whisper">
                <Stack gap={1}>
                  <Row gap={2}>
                    <Text variant="small" className="text-positive font-medium">{result.inserted} inserted</Text>
                    {result.skipped_duplicates > 0 && <Text variant="small" className="text-muted">{result.skipped_duplicates} skipped (duplicates)</Text>}
                    {result.errors.length > 0 && <Text variant="small" className="text-negative">{result.errors.length} errors</Text>}
                  </Row>
                  {result.errors.slice(0, 5).map((e: any, i: number) => (
                    <Text key={i} variant="label" className="text-negative">row {e.row}: {e.message}</Text>
                  ))}
                  {result.inserted > 0 && <Text variant="label" muted>blacklist sweep running in background &mdash; flagged deals will update shortly.</Text>}
                </Stack>
              </Card>
            )}

            <Row gap={1} justify="end">
              <Button variant="ghost" size="sm" onClick={onClose}>cancel</Button>
              <Button variant="primary" size="sm" disabled={!csvText.trim() || busy || !!result} onClick={submit}>
                {busy ? 'importing…' : 'import'}
              </Button>
            </Row>
          </Stack>
        </div>
      </div>
    </div>
  );
}

// ─── GroupLists panel ─────────────────────────────────────────────────────────
export function GroupLists() {
  const [tab, setTab] = useState<Kind>('blacklist');
  const [entries, setEntries] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showImport, setShowImport] = useState(false);
  const [draft, setDraft] = useState({ targetName: '', reason: '', jurisdiction: '' });
  const [addBusy, setAddBusy] = useState(false);
  const [addErr, setAddErr] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    try {
      const r = await api.listEntries(tab);
      setEntries(r.entries);
    } finally { setLoading(false); }
  }

  useEffect(() => { load(); }, [tab]);

  async function addEntry() {
    if (!draft.targetName.trim() || !draft.reason.trim()) return;
    setAddBusy(true); setAddErr(null);
    try {
      await api.addListEntry(tab, { targetName: draft.targetName.trim(), reason: draft.reason.trim(), jurisdiction: draft.jurisdiction.trim() || undefined });
      setDraft({ targetName: '', reason: '', jurisdiction: '' });
      await load();
    } catch (e: any) {
      setAddErr(e.message);
    } finally { setAddBusy(false); }
  }

  async function removeEntry(id: string) {
    if (!confirm('Remove this entry?')) return;
    await api.removeListEntry(tab, id);
    await load();
  }

  return (
    <>
      {showImport && (
        <CsvImportModal
          kind={tab}
          onClose={() => setShowImport(false)}
          onDone={() => { setShowImport(false); load(); }}
        />
      )}

      <Panel
        title="Group lists"
        action={
          <Row gap={1}>
            {(['blacklist', 'whitelist'] as Kind[]).map(k => (
              <button
                key={k}
                onClick={() => setTab(k)}
                className={clsx('text-xs px-2 py-1 rounded-sm hairline transition-colors', tab === k ? 'bg-ink text-bg' : 'text-muted hover:text-ink')}
              >{k}</button>
            ))}
          </Row>
        }
      >
        <Stack gap={3}>
          <Text variant="small" muted>
            {tab === 'blacklist'
              ? 'entities on this list will trigger a compliance alert whenever they appear as a party on any deal.'
              : 'entities on this list are pre-approved counterparties for your group.'}
          </Text>

          <Row gap={1} justify="end">
            <Button variant="secondary" size="sm" onClick={() => setShowImport(true)}>import csv</Button>
          </Row>

          {loading ? <Text variant="label">loading...</Text>
            : entries.length === 0 ? (
              <EmptyState
                title={`no ${tab} entries yet`}
                hint="add entries below or import a CSV"
              />
            ) : (
              <List>
                {entries.map((e: any) => (
                  <ListItem key={e.id}>
                    <Row justify="between" align="start">
                      <Stack gap={0.5}>
                        <Row gap={1} align="center">
                          <Text variant="small" className="font-medium">{e.targetName}</Text>
                          {e.targetType && <Tag>{e.targetType}</Tag>}
                          {e.jurisdiction && <Text variant="label">{e.jurisdiction}</Text>}
                        </Row>
                        <Text variant="label">{e.reason}</Text>
                        {e.identifierValue && <Text variant="label">{e.identifierType}: {e.identifierValue}</Text>}
                        {e.source && <Text variant="label">source: {e.source}</Text>}
                      </Stack>
                      <Button variant="ghost" size="sm" onClick={() => removeEntry(e.id)}>remove</Button>
                    </Row>
                  </ListItem>
                ))}
              </List>
            )}

          <Divider />
          <Stack gap={2}>
            <Text variant="micro">add entry</Text>
            <Row gap={1} wrap>
              <Input value={draft.targetName} onChange={e => setDraft({ ...draft, targetName: e.target.value })} placeholder="entity name *" className="flex-1 min-w-[160px]" />
              <Input value={draft.reason} onChange={e => setDraft({ ...draft, reason: e.target.value })} placeholder="reason *" className="flex-1 min-w-[160px]" />
              <Input value={draft.jurisdiction} onChange={e => setDraft({ ...draft, jurisdiction: e.target.value })} placeholder="country (optional)" className="w-40" />
              <Button variant="primary" size="sm" disabled={addBusy || !draft.targetName.trim() || !draft.reason.trim()} onClick={addEntry}>
                {addBusy ? 'adding...' : 'add'}
              </Button>
            </Row>
            {addErr && <Text variant="label" className="text-negative">{addErr}</Text>}
          </Stack>
        </Stack>
      </Panel>
    </>
  );
}
